import {SettlementStatus, settlementStatus, TimeGroup, toSettlementStatus} from "@ots-frontend/ots-data";
import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Modal, ModalProps, TextInput} from "@mantine/core";
import {useCallback} from "react";
import {SettlementStatusSelect} from "../../select/entity/SettlementStatusSelect/SettlementStatusSelect";

interface SettlementFormProps {
  options?: SettlementFormOptions;
  defaultValues?: Partial<SettlementFormValues>;
  onSubmit?: (values: SettlementFormValues) => void;
  submitting?: boolean;
}

export interface SettlementFormOptions {
  // props here
  regionId?: string
}

export interface SettlementFormValues {
  name?: string
  status: SettlementStatus
}

const getDefaultValues = (defaultValues: Partial<SettlementFormValues> | undefined) => {
  return {
    name: defaultValues?.name,
    status: defaultValues?.status?.code ?? settlementStatus.open.code,
  }
}

export const SettlementForm = (props: SettlementFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: getDefaultValues(defaultValues),
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        name: values.name,
        status: toSettlementStatus(values.status!),
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <TextInput
        label={t("common.entity.settlement.form.field.name", "Name")}
        placeholder={t<string>("common.entity.settlement.form.field.name.placeholder", "Name")}
        styles={{wrapper: {width: "100%"}}}
        {...form.getInputProps("name")} />
      <Divider my="sm" variant="dotted"/>
      <SettlementStatusSelect
        label={t("common.entity.settlement.form.field.status", "Status")}
        placeholder={t<string>("common.entity.settlement.form.field.status.placeholder", "Status")}
        styles={{wrapper: {width: "100%"}}}
        withAsterisk={true}
        required={true}
        clearable={true}
        withinPortal={true}
        {...form.getInputProps("status")} />
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface SettlementFormModalProps extends SettlementFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const SettlementFormModal = (props: SettlementFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <SettlementForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
